import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Separator } from '../ui/separator';
import { Badge } from '../ui/badge';
import { toast } from 'sonner';
import axios from 'axios';
import { API } from '../../App';
import { User, Camera, Loader2, Check, Building2, Globe, LogOut, Upload, CalendarDays, Edit, AlertTriangle, CheckCircle2, Mail, TrendingDown, ChevronRight } from 'lucide-react';
import { motion } from 'framer-motion';
import { format, subDays } from 'date-fns';
import { BacklogChoiceSection } from './BacklogChoiceSection';
import { LegalFormCard } from '../legal-form/LegalFormCard';

const BUSINESS_TYPES = {
  trade: 'Trade - I sell goods / products',
  service: 'Service - I sell services',
  mixed: 'Mixed - goods and services',
};

const INDUSTRIES = [
  'Retail & E-commerce', 'Professional Services', 'Food & Restaurant',
  'Technology & Software', 'Healthcare & Wellness', 'Construction & Real Estate',
  'Manufacturing', 'Transportation & Logistics', 'Education & Training',
  'Fashion & Beauty', 'Agriculture', 'Media & Entertainment',
  'Financial Services', 'Hospitality & Tourism', 'Non-Profit', 'Other',
];

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

// iter1077 - Compact fiscal year end selector.  The selected month is the
// one that, when closed via month-end, ALSO triggers the year-end
// retained-earnings journal.
const FiscalYearEndCard = ({ business, onSaved }) => {
  const current = String(business?.financial_year_end || '12');
  const [draft, setDraft] = useState(current);
  const [saving, setSaving] = useState(false);
  const dirty = draft !== current;

  useEffect(() => { setDraft(current); }, [current]);

  const save = async () => {
    setSaving(true);
    try {
      await axios.patch(`${API}/businesses/profile`, { financial_year_end: draft });
      toast.success(`Fiscal year end set to ${MONTH_NAMES[parseInt(draft, 10) - 1]}`);
      onSaved && onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not update fiscal year end');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-4 bg-indigo-50/60 rounded-lg border border-indigo-200" data-testid="fiscal-year-end-card" data-highlight-key="fiscal-year-end">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="min-w-0">
          <p className="text-xs text-indigo-700 uppercase font-semibold mb-1">Fiscal Year End</p>
          <p className="text-sm text-slate-700">
            Closing <b>{MONTH_NAMES[parseInt(draft, 10) - 1] || 'December'}</b> also runs your year-end close and locks the year.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <select
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            className="px-3 py-2 rounded-md border border-slate-300 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-indigo-400 focus:border-indigo-400"
            data-testid="fiscal-year-end-select"
          >
            {MONTH_NAMES.map((n, i) => (
              <option key={i + 1} value={String(i + 1)}>{n}</option>
            ))}
          </select>
          <Button
            size="sm"
            onClick={save}
            disabled={!dirty || saving}
            className="bg-indigo-600 hover:bg-indigo-700"
            data-testid="save-fiscal-year-end-btn"
          >
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Save'}
          </Button>
        </div>
      </div>
    </div>
  );
};

const EditableField = ({ label, value, onSave, options = null, testId }) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value || '');
  const [saving, setSaving] = useState(false);

  useEffect(() => { setDraft(value || ''); }, [value]);

  const save = async () => {
    const v = draft.trim();
    if (!v || v === value) { setEditing(false); return; }
    setSaving(true);
    try {
      await onSave(v);
      setEditing(false);
    } finally {
      setSaving(false);
    }
  };

  if (!editing) {
    return (
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-muted-foreground">{label}</p>
          <p className="font-medium text-foreground" data-testid={`${testId}-value`}>{value || '-'}</p>
        </div>
        <Button variant="ghost" size="sm" onClick={() => setEditing(true)} data-testid={`${testId}-edit`}>
          <Edit className="w-4 h-4" />
        </Button>
      </div>
    );
  }
  return (
    <div>
      <p className="text-sm text-muted-foreground mb-1">{label}</p>
      <div className="flex items-center gap-2">
        {options ? (
          <select
            className="flex-1 border rounded-md px-3 py-2 text-sm bg-background"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            data-testid={`${testId}-input`}
          >
            {options.map(o => <option key={o} value={o}>{o}</option>)}
          </select>
        ) : (
          <Input value={draft} onChange={(e) => setDraft(e.target.value)}
                 className="flex-1" data-testid={`${testId}-input`}
                 onKeyDown={(e) => e.key === 'Enter' && save()} autoFocus />
        )}
        <Button size="sm" onClick={save} disabled={saving}
                className="bg-[#108981] hover:bg-[#108981]/90" data-testid={`${testId}-save`}>
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
        </Button>
        <Button size="sm" variant="ghost" onClick={() => { setDraft(value || ''); setEditing(false); }}
                data-testid={`${testId}-cancel`}>
          Cancel
        </Button>
      </div>
    </div>
  );
};

const GeneralSettingsTab = ({ user, business, refreshBusiness, refreshUser, logout }) => {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  // Local overrides so edits show instantly (user prop refreshes on next /auth/me)
  const [accountName, setAccountName] = useState(user?.full_name || '');
  useEffect(() => { setAccountName(user?.full_name || ''); }, [user?.full_name]);

  const saveAccountName = async (v) => {
    try {
      await axios.patch(`${API}/auth/profile`, { full_name: v });
      setAccountName(v);
      refreshUser?.();
      toast.success('Account name updated');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update name');
      throw e;
    }
  };

  const [businessClass, setBusinessClass] = useState(business?.business_class || null);
  useEffect(() => {
    axios.get(`${API}/business/class`).then((r) => setBusinessClass(r.data?.business_class || 'mixed')).catch(() => {});
  }, [business?.id]);

  const saveBusinessClass = async (label) => {
    const code = Object.keys(BUSINESS_TYPES).find((k) => BUSINESS_TYPES[k] === label) || 'mixed';
    try {
      await axios.patch(`${API}/business/class`, { business_class: code });
      setBusinessClass(code);
      toast.success('Business type updated');
      refreshBusiness?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update business type');
      throw e;
    }
  };

  const saveBusinessProfile = async (patch, label) => {
    try {
      await axios.patch(`${API}/business/profile`, patch);
      toast.success(`${label} updated`);
      refreshBusiness?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || `Failed to update ${label.toLowerCase()}`);
      throw e;
    }
  };
  
  // Reply-To Email state
  const [replyToEmail, setReplyToEmail] = useState('');
  const [savingReplyTo, setSavingReplyTo] = useState(false);
  const [replyToLoaded, setReplyToLoaded] = useState(false);

  // Fetch email settings on mount
  useEffect(() => {
    const fetchEmailSettings = async () => {
      try {
        const response = await axios.get(`${API}/business/settings/email`);
        setReplyToEmail(response.data.reply_to_email || '');
        setReplyToLoaded(true);
      } catch (error) {
        console.log('Email settings not yet configured');
        setReplyToLoaded(true);
      }
    };
    fetchEmailSettings();
  }, []);

  // Save reply-to email
  const saveReplyToEmail = async () => {
    const trimmedEmail = replyToEmail.trim();
    if (trimmedEmail && !trimmedEmail.includes('@')) {
      toast.error('Please enter a valid email address');
      return;
    }
    
    setSavingReplyTo(true);
    try {
      await axios.patch(`${API}/business/settings/email`, {
        reply_to_email: trimmedEmail
      });
      toast.success(trimmedEmail 
        ? 'Reply-to email saved! Customer replies will now go to this address.' 
        : 'Reply-to email removed.');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save reply-to email');
    } finally {
      setSavingReplyTo(false);
    }
  };

  // Calculate opening balance date - defensive: catch invalid date strings
  let openingBalanceDate = null;
  try {
    if (business?.accounting_start_date) {
      const d = new Date(business.accounting_start_date);
      if (!isNaN(d.getTime())) {
        openingBalanceDate = format(subDays(d, 1), 'yyyy-MM-dd');
      }
    }
  } catch {
    // date-fns throws RangeError on invalid dates
  }

  // Country helpers
  const getCountryFlag = (code) => {
    const flags = {
      'NG': '🇳🇬', 'GH': '🇬🇭', 'KE': '🇰🇪', 'ZA': '🇿🇦', 'TZ': '🇹🇿',
      'GB': '🇬🇧', 'US': '🇺🇸', 'DE': '🇩🇪', 'FR': '🇫🇷', 'NL': '🇳🇱',
      'IE': '🇮🇪', 'IT': '🇮🇹', 'BE': '🇧🇪', 'AT': '🇦🇹', 'DK': '🇩🇰',
      'FI': '🇫🇮', 'EE': '🇪🇪', 'LV': '🇱🇻', 'LT': '🇱🇹'
    };
    return flags[code] || '🌍';
  };

  const getCountryName = (code) => {
    const names = {
      'NG': 'Nigeria', 'GH': 'Ghana', 'KE': 'Kenya', 'ZA': 'South Africa', 'TZ': 'Tanzania',
      'GB': 'United Kingdom', 'US': 'United States', 'DE': 'Germany', 'FR': 'France',
      'NL': 'Netherlands', 'IE': 'Ireland', 'IT': 'Italy', 'BE': 'Belgium', 'AT': 'Austria',
      'DK': 'Denmark', 'FI': 'Finland', 'EE': 'Estonia', 'LV': 'Latvia', 'LT': 'Lithuania'
    };
    return names[code] || code;
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (!file.type.startsWith('image/')) {
      toast.error('Please upload an image file');
      return;
    }
    
    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const response = await axios.post(`${API}/business/upload-logo`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      toast.success('Logo updated successfully!');
      refreshBusiness?.();
    } catch (error) {
      console.error('Logo upload error:', error);
      toast.error(error.response?.data?.detail || 'Failed to upload logo');
    } finally {
      setUploading(false);
    }
  };

  const handleLogout = () => {
    logout?.();
    toast.success('Logged out successfully');
  };

  return (
    <div className="space-y-6">
      {/* Business Logo & Branding */}
      <Card data-testid="branding-card">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Camera className="w-5 h-5 text-[#5FC6C5]" />
            Business Branding
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-6">
            <div className="relative group">
              {business?.logo_url ? (
                <img 
                  src={business.logo_url} 
                  alt={business.business_name} 
                  className="w-24 h-24 rounded-xl object-cover border-2 border-border"
                />
              ) : (
                <div className="w-24 h-24 rounded-xl bg-gradient-to-br from-[#283F8B] to-[#108981] flex items-center justify-center">
                  <span className="text-white font-bold text-3xl">{business?.business_name?.charAt(0) || 'B'}</span>
                </div>
              )}
              <motion.button
                onClick={() => fileInputRef.current?.click()}
                className="absolute inset-0 bg-black/50 rounded-xl flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                disabled={uploading}
              >
                {uploading ? (
                  <Loader2 className="w-6 h-6 text-white animate-spin" />
                ) : (
                  <Upload className="w-6 h-6 text-white" />
                )}
              </motion.button>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={handleLogoUpload}
              />
            </div>

            <div className="flex-1">
              <h3 className="font-medium text-foreground mb-1">{business?.business_name}</h3>
              <p className="text-sm text-muted-foreground mb-3">
                Shown on invoices, bills, payslips, payment pages and reports.
              </p>
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
              >
                {uploading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Uploading...
                  </>
                ) : (
                  <>
                    <Upload className="w-4 h-4 mr-2" />
                    {business?.logo_url ? 'Change Logo' : 'Upload Logo'}
                  </>
                )}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Account Info */}
      <Card data-testid="account-card">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <User className="w-5 h-5 text-[#283F8B]" />
            Account
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <EditableField label="Name" value={accountName} onSave={saveAccountName}
                         testId="account-name" />
          <Separator />
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground">Email</p>
              <p className="font-medium text-foreground">{user?.email}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Business Info */}
      <Card data-testid="business-card">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Building2 className="w-5 h-5 text-[#108981]" />
            Business
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <EditableField label="Business Name" value={business?.business_name}
                         onSave={(v) => saveBusinessProfile({ business_name: v }, 'Business name')}
                         testId="business-name" />
          <Separator />
          <EditableField label="Industry" value={business?.industry}
                         options={INDUSTRIES}
                         onSave={(v) => saveBusinessProfile({ industry: v }, 'Industry')}
                         testId="business-industry" />
          <Separator />
          <div data-highlight-key="business-type" className="rounded-lg -mx-2 px-2 py-1 transition-shadow" data-testid="business-type-field">
            <EditableField label="Business Type" value={BUSINESS_TYPES[businessClass] || BUSINESS_TYPES.mixed}
                           options={Object.values(BUSINESS_TYPES)}
                           onSave={saveBusinessClass}
                           testId="business-type" />
            <p className="text-xs text-muted-foreground mt-1">
              Trade / Mixed turns on Inventory; service-only hides it.
            </p>
          </div>
          <Separator />
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Globe className="w-4 h-4 text-muted-foreground" />
              <div>
                <p className="text-sm text-muted-foreground">Country</p>
                <p className="font-medium text-foreground">
                  {getCountryFlag(business?.country)} {getCountryName(business?.country)}
                </p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Locked: tax rules and your ledger are country-specific
                </p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <LegalFormCard onChanged={() => refreshBusiness?.()} />

      {/* Recognition Policy (depreciation & amortization) */}
      <Card data-testid="recognition-policy-card">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <TrendingDown className="w-5 h-5 text-[#108981]" />
            Recognition Policy
          </CardTitle>
          <CardDescription>
            Depreciation conventions, posting day and useful life per asset type
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Button
            variant="outline"
            onClick={() => navigate('/settings/recognition-policy')}
            data-testid="open-recognition-policy-btn"
          >
            Manage Recognition Policy
            <ChevronRight className="w-4 h-4 ml-2" />
          </Button>
        </CardContent>
      </Card>

      {/* Auto-Capture Start Month */}
      <Card data-testid="accounting-start-date-card" data-highlight-fallback="fiscal-year-end">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <CalendarDays className="w-5 h-5 text-[#108981]" />
            Financial Capture Period
          </CardTitle>
          <CardDescription>
            The month from which we automatically capture your financial activity
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {business?.platform_activated ? (
            <>
              <div className="flex items-center justify-between p-4 bg-green-50 rounded-lg border border-green-200">
                <div className="flex items-center gap-3">
                  <CheckCircle2 className="w-5 h-5 text-green-600" />
                  <div>
                    <p className="font-medium text-green-900">Auto-Capture Active</p>
                    <p className="text-sm text-green-700">
                      Financial data captured automatically since {(() => { try { return new Intl.DateTimeFormat('en', {month:'long',year:'numeric'}).format(new Date(business.accounting_start_date)); } catch { return business.accounting_start_date; }})()}
                    </p>
                  </div>
                </div>
                <Badge className="bg-green-600">Active</Badge>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="p-3 bg-slate-50 rounded-lg border">
                  <p className="text-xs text-slate-500 uppercase mb-1">Auto-Capture Start Month</p>
                  <p className="text-lg font-mono font-bold text-slate-900">
                    {(() => { try { return new Intl.DateTimeFormat('en', {month:'long',year:'numeric'}).format(new Date(business.accounting_start_date)); } catch { return business.accounting_start_date; }})()}
                  </p>
                  <p className="text-xs text-slate-500 mt-1">Transactions tracked from this month</p>
                </div>
                <div className="p-3 bg-slate-50 rounded-lg border">
                  <p className="text-xs text-slate-500 uppercase mb-1">Opening Balance Date</p>
                  <p className="text-lg font-mono font-bold text-slate-900">
                    {(() => { const d = business.opening_balance_date || openingBalanceDate; try { return new Intl.DateTimeFormat('en', {month:'long',year:'numeric'}).format(new Date(d)); } catch { return d; }})()}
                  </p>
                  <p className="text-xs text-slate-500 mt-1">Your financial starting point</p>
                </div>
              </div>

              {/* iter1077 - Fiscal Year End picker. Determines which month
                  triggers the annual retained-earnings journal during
                  month-end close. */}
              <FiscalYearEndCard business={business} onSaved={refreshBusiness} />

              <div className="p-3 bg-amber-50 rounded-lg border border-amber-200">
                <div className="flex items-start gap-2">
                  <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-sm text-amber-800">
                      Changing the start month may affect existing records and <strong>cannot be undone easily</strong>.
                    </p>
                  </div>
                </div>
              </div>
              
              <Button 
                variant="outline"
                onClick={() => navigate('/settings/start-date?change=true')}
                data-testid="change-start-date-settings-btn"
              >
                <Edit className="w-4 h-4 mr-2" />
                Change Start Month
              </Button>
            </>
          ) : (
            <>
              <div className="flex items-center justify-between p-4 bg-amber-50 rounded-lg border border-amber-200">
                <div className="flex items-center gap-3">
                  <AlertTriangle className="w-5 h-5 text-amber-600" />
                  <div>
                    <p className="font-medium text-amber-900">Setup Required</p>
                    <p className="text-sm text-amber-700">
                      Choose the month we start tracking from
                    </p>
                  </div>
                </div>
                <Badge variant="outline" className="border-amber-400 text-amber-700">Required</Badge>
              </div>
              
              <Button 
                className="bg-[#108981] hover:bg-[#108981]/90"
                onClick={() => navigate('/settings/start-date')}
                data-testid="set-start-date-settings-btn"
              >
                <CalendarDays className="w-4 h-4 mr-2" />
                Set Auto-Capture Start Month
              </Button>
            </>
          )}

          <BacklogChoiceSection />
        </CardContent>
      </Card>

      {/* Reply-To Email Settings */}
      <Card data-testid="reply-to-email-card">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Mail className="w-5 h-5 text-[#283F8B]" />
            Reply-To Email
          </CardTitle>
          <CardDescription>
            Customer replies to invoices and receipts go here.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="reply-to-email">Your Business Email</Label>
            <div className="flex gap-3">
              <Input
                id="reply-to-email"
                type="email"
                value={replyToEmail}
                onChange={(e) => setReplyToEmail(e.target.value)}
                placeholder="e.g., accounts@yourbusiness.com"
                className="flex-1"
                data-testid="reply-to-email-input"
                disabled={!replyToLoaded}
              />
              <Button
                onClick={saveReplyToEmail}
                disabled={savingReplyTo || !replyToLoaded}
                className="bg-[#108981] hover:bg-[#108981]/90"
                data-testid="save-reply-to-btn"
              >
                {savingReplyTo ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Check className="w-4 h-4 mr-2" />
                    Save
                  </>
                )}
              </Button>
            </div>
            <p className="text-xs text-muted-foreground">
              Used on all outgoing emails.
            </p>
          </div>
          
          {replyToEmail && replyToLoaded && (
            <div className="p-3 bg-green-50 rounded-lg border border-green-200">
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-green-600" />
                <p className="text-sm text-green-800">
                  Replies will go to: <strong>{replyToEmail}</strong>
                </p>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Logout */}
      <Card className="border-destructive/20">
        <CardContent className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium text-foreground">Sign Out</p>
              <p className="text-sm text-muted-foreground">Sign out of your account</p>
            </div>
            <Button 
              variant="destructive" 
              onClick={handleLogout}
              data-testid="settings-logout-btn"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Sign Out
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default GeneralSettingsTab;
